/** A web search QuantisAI asked for. Nothing leaves Quantis until the user has read the exact question and chosen to send it. */
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../shared/ui/button';
import { SIWebLookupError, decideSIWebLookup, type SIActionProposal, type SIProposalState } from './siActions';

type CardState = SIProposalState | 'used';
const SPENT: Partial<Record<CardState, string>> = {
 confirmed: 'Searched', skipped: 'Skipped', expired: 'Expired, ask again', failed: 'Search failed, ask again', used: 'Already used',
};
const HEADING: Partial<Record<CardState, string>> = { confirmed: 'Web search approved', skipped: 'Web search skipped' };

/** onApproved continues the chat once the search has run; it is not called on a card restored from history. */
export function SIWebLookupCard({ workspaceId, proposal, initialState = 'pending', onApproved }: {
 workspaceId: string; proposal: SIActionProposal; initialState?: SIProposalState; onApproved?: (lookupId: string) => void;
}) {
 const [state, setState] = useState<CardState>(initialState);
 const [busy, setBusy] = useState<'search' | 'skip' | null>(null);
 const [error, setError] = useState<string | null>(null);
 const controller = useRef<AbortController | null>(null);
 useEffect(() => () => controller.current?.abort(), []);
 const decide = async (decision: 'search' | 'skip') => {
  if (busy || state !== 'pending') return;
  const abort = new AbortController(); controller.current = abort; setBusy(decision); setError(null);
  try {
   const { lookupId } = await decideSIWebLookup(workspaceId, proposal, decision, abort.signal);
   if (abort.signal.aborted) return;
   setState(decision === 'search' ? 'confirmed' : 'skipped');
   if (decision === 'search') onApproved?.(lookupId);
  } catch (cause) {
   if (abort.signal.aborted) return;
   setError(cause instanceof Error ? cause.message : 'The web search could not be completed. Ask QuantisAI again.');
   // The server has settled these for good; anything else, such as a dropped connection, may be tried again.
   if (cause instanceof SIWebLookupError) {
    if (cause.status === 410) setState('expired');
    else if (cause.status === 409) setState('used');
    else if (cause.status === 502 || cause.status === 404) setState('failed');
   }
  } finally { if (!abort.signal.aborted) setBusy(null); }
 };
 const spent = SPENT[state];
 return <section aria-label="Web search" className="mb-4 rounded-lg border p-3 text-sm">
  <p className="text-xs text-muted-foreground">{HEADING[state] ?? 'Web search'} · {proposal.subtitle}</p>
  <blockquote className="mt-2 whitespace-pre-wrap border-l-2 pl-3 [overflow-wrap:anywhere]">{proposal.body}</blockquote>
  <p className="mt-2 text-xs text-muted-foreground">{proposal.workflowNotice || 'This question will be sent to a public web search.'}</p>
  {spent ? <Button type="button" size="sm" variant="outline" className="mt-2" disabled>{spent}</Button>
   : <div className="mt-2 flex gap-2">
    <Button type="button" size="sm" disabled={busy !== null} onClick={() => void decide('search')}>{busy === 'search' ? 'Searching...' : 'Search the web'}</Button>
    <Button type="button" size="sm" variant="outline" disabled={busy !== null} onClick={() => void decide('skip')}>Skip</Button>
   </div>}
  {error && <p role="alert" className="mt-2 text-destructive">{error}</p>}
 </section>;
}
